import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import JsonDialog from "../../FilledForms/JsonDialog";
import { fetchUserCreatedFormFilledForms } from "../UserCreatedFormsList/api";
import "./UserFilledFormsDialog.css";

const UserCreatedFilledFormsDialog = ({
  visible,
  onHide,
  formId,
  formTitle,
}) => {
  const [filledForms, setFilledForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [jsonDialogVisible, setJsonDialogVisible] = useState(false);
  const [selectedJsonForm, setSelectedJsonForm] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (!formId) {
          console.error("Form ID is missing");
          setLoading(false);
          return;
        }

        const response = await fetchUserCreatedFormFilledForms(formId);

        // Transform the data to match the expected format
        const transformedForms = response.map((form) => ({
          id: form.id,
          formTitle: form.formTitle,
          submittedAt: form.createdAt,
          data: form.data,
          submissionId: form.submissionId,
        }));

        setFilledForms(transformedForms);
        setFilteredData(transformedForms);
      } catch (error) {
        console.error("Error fetching filled forms:", error);
        setFilledForms([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    if (visible && formId) {
      fetchData();
    }
  }, [visible, formId]);

  useEffect(() => {
    if (filledForms.length > 0) {
      let filtered = [...filledForms];

      if (globalFilterValue) {
        filtered = filledForms.filter((form) => {
          const formattedDate = form.submittedAt
            ? new Date(form.submittedAt).toLocaleString()
            : "";

          const submissionId = form.submissionId
            ? form.submissionId.toString()
            : "";

          return (
            (form.formTitle &&
              form.formTitle
                .toLowerCase()
                .includes(globalFilterValue.toLowerCase())) ||
            formattedDate
              .toLowerCase()
              .includes(globalFilterValue.toLowerCase()) ||
            submissionId.includes(globalFilterValue)
          );
        });
      }

      if (sortField) {
        filtered.sort((a, b) => {
          let valueA = a[sortField];
          let valueB = b[sortField];

          if (typeof valueA === "string") {
            valueA = valueA.toLowerCase();
          }

          if (typeof valueB === "string") {
            valueB = valueB.toLowerCase();
          }

          if (valueA < valueB) {
            return sortOrder * -1;
          }
          if (valueA > valueB) {
            return sortOrder;
          }
          return 0;
        });
      }

      setFilteredData(filtered);
    }
  }, [globalFilterValue, filledForms, sortField, sortOrder]);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  const onGlobalFilterChange = (e) => {
    setGlobalFilterValue(e.target.value);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  const viewJson = (rowData) => {
    setSelectedJsonForm(rowData);
    setJsonDialogVisible(true);
  };

  const header = (
    <div className="table-header">
      <h3>Filled Forms for: {formTitle}</h3>
      <div className="filter-container">
        <IconField iconPosition="right">
          <InputIcon className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search forms"
            style={{ fontFamily: "poppins" }}
          />
        </IconField>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          onClick={clearFilter}
          style={{
            backgroundColor: "#D97A07",
            border: "none",
            color: "#fff",
            marginLeft: "10px",
            fontFamily: "poppins",
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        header="Submitted Forms"
        visible={visible}
        style={{ width: window.innerWidth > 768 ? "80vw" : "95vw" }}
        onHide={onHide}
        maximizable
        modal
      >
        <div className="filled-forms-dialog-content">
          {loading ? (
            <div className="loader-container">
              <ProgressSpinner
                style={{ width: "50px", height: "50px" }}
                strokeWidth="4"
                animationDuration="1s"
              />
            </div>
          ) : (
            <DataTable
              value={filteredData}
              header={header}
              paginator
              rows={10}
              emptyMessage="No submitted forms found"
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
              responsiveLayout="scroll"
              breakpoint="400px"
              style={{ fontFamily: "poppins" }}
              className="user-filled-forms-table"
            >
              <Column
                field="formTitle"
                header="Form Title"
                sortable
                style={{ width: "25%" }}
              />
              <Column
                field="submittedAt"
                header="Submitted At"
                body={(rowData) => formatDate(rowData.submittedAt)}
                sortable
                style={{ width: "25%" }}
              />
              <Column
                field="submissionId"
                header="Submitted By"
                sortable
                style={{ width: "20%" }}
              />
              <Column
                header="Actions"
                style={{ width: "15%" }}
                body={(rowData) => (
                  <div className="form-actions">
                    <Button
                      icon="pi pi-code"
                      className="p-button p-button-info mr-2"
                      style={{
                        backgroundColor: "#fff",
                        color: "#0d2e4d",
                        border: "none",
                      }}
                      onClick={() => viewJson(rowData)}
                      tooltip="View JSON data"
                      tooltipOptions={{ position: "top" }}
                    />
                  </div>
                )}
              />
            </DataTable>
          )}
        </div>
      </Dialog>

      <JsonDialog
        visible={jsonDialogVisible}
        onHide={() => setJsonDialogVisible(false)}
        jsonForm={selectedJsonForm}
      />
    </>
  );
};

export default UserCreatedFilledFormsDialog;
